import React, { Fragment, useState, useEffect } from 'react';
import {
  Dialog,
  Transition,
  Combobox,
  TransitionChild,
  DialogPanel,
  DialogTitle,
  ComboboxInput, ComboboxOptions, ComboboxOption
} from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import type { Customer, CustomerWithoutId, OrderFormData } from '../types';
import { fetchActiveDeliveryOptionsFromDb, type DeliveryOption } from '../services/deliveryService';
import { fetchCollectionPointsFromDb } from '../services/collectionPointService';
import { fetchProductsByStoreFromDb, type ProductInventoryItem } from '../services/productService';
import OrderItemsSection, { type OrderLineItem } from './OrderItemsSection';

interface CreateOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderData: OrderFormData;
  newCustomer: Omit<CustomerWithoutId, 'id'>;
  showNewCustomerForm: boolean;
  customers?: Customer[] | null;
  onOrderChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onOrderItemsChange: (items: OrderFormData['order_items']) => void;
  onCustomerChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onToggleCustomerForm: () => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  stores?: { id: string; name: string }[] | null;
}

type CollectionPoint = { id: string; name: string; address_line1: string };

const FIELD = 'block w-full rounded-lg border-0 ring-1 ring-inset ring-stone-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-stone-400 focus:ring-2 focus:ring-gray-900 focus:outline-none';
const LABEL = 'block text-sm font-medium text-gray-800';

const CreateOrderModal = ({
                            isOpen, onClose, orderData, newCustomer, showNewCustomerForm,
                            customers = [], onOrderChange, onOrderItemsChange, onCustomerChange,
                            onToggleCustomerForm, onSubmit, loading, stores,
                          }: CreateOrderModalProps) => {
  const [query,             setQuery]             = useState('');
  const [allProducts,       setAllProducts]       = useState<ProductInventoryItem[]>([]);
  const [deliveryOptions,   setDeliveryOptions]   = useState<DeliveryOption[]>([]);
  const [collectionPoints,  setCollectionPoints]  = useState<CollectionPoint[]>([]);
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);

  const isCollectionSelected = orderData.delivery_type === 'collection';

  const filteredCustomers = query === ''
      ? customers
      : customers?.filter(c =>
          `${c.first_name} ${c.last_name}`.toLowerCase().includes(query.toLowerCase())
      );

  useEffect(() => {
    if (!orderData.store_id) { setAllProducts([]); return; }
    void fetchProductsByStoreFromDb(orderData.store_id)
        .then(setAllProducts)
        .catch(() => setAllProducts([]));
  }, [orderData.store_id]);

  useEffect(() => {
    if (!isOpen) return;
    void Promise.all([
      fetchActiveDeliveryOptionsFromDb(),
      fetchCollectionPointsFromDb(),
    ]).then(([opts, points]) => {
      setDeliveryOptions(opts);
      setCollectionPoints(points.map(p => ({
        id:           p.id,
        name:         p.name,
        address_line1: (p as CollectionPoint).address_line1 ?? '',
      })));
    }).catch(() => {
      setDeliveryOptions([]);
      setCollectionPoints([]);
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      onOrderItemsChange([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const orderItems: OrderLineItem[] = (orderData.order_items ?? []).map((i) => ({
    product_id: i.product_id,
    product_name: i.product_name,
    sku: i.sku,
    quantity: i.quantity,
    unit_price: i.unit_price,
    product_prices_id: i.product_prices_id,
  }));

  const handleItemsChange = (items: OrderLineItem[]) => {
    onOrderItemsChange(items.map((i) => ({
      product_id: i.product_id,
      product_name: i.product_name,
      sku: i.sku,
      quantity: i.quantity,
      unit_price: i.unit_price,
      product_prices_id: i.product_prices_id,
    })));
  };

  const isSubmitDisabled =
      loading ||
      !orderData.delivery_type ||
      (isCollectionSelected && !orderData.collection_point_id) ||
      (orderItems.length === 0 && !orderData.notes?.trim());

  return (
      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <TransitionChild as={Fragment}
                           enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
                           leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-[1px]" />
          </TransitionChild>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
              <TransitionChild as={Fragment}
                               enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100"
                               leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-2 sm:scale-95">
                <DialogPanel className="relative w-full transform rounded-2xl bg-white ring-1 ring-stone-200 shadow-xl transition-all sm:max-w-2xl flex flex-col max-h-[90vh]">
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button type="button" onClick={onClose} aria-label="Close"
                            className="rounded-md text-stone-400 hover:text-stone-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900">
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="px-5 py-6 sm:px-6 overflow-y-auto flex-1">
                    <DialogTitle as="h3" className="font-display text-xl tracking-tight text-gray-900">
                      Capture new order
                    </DialogTitle>
                    <p className="mt-1 text-sm text-gray-600">Fill in the order details and assign a customer.</p>

                    <form onSubmit={e => { e.preventDefault(); onSubmit(e); }} className="mt-6 space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="reference" className={LABEL}>Reference</label>
                          <input type="text" id="reference" name="reference" required
                                 value={orderData.reference} onChange={onOrderChange}
                                 className={FIELD} placeholder="e.g. INV-1045" />
                        </div>

                        <div>
                          <label htmlFor="parcel_count" className={LABEL}>Parcel count</label>
                          <input type="number" id="parcel_count" name="parcel_count" min={1} required
                                 value={orderData.parcel_count === 0 ? '' : orderData.parcel_count}
                                 onChange={onOrderChange} className={FIELD} />
                        </div>

                        <div className="col-span-2">
                          <label className={LABEL}>Store Location</label>
                          <div className="relative">
                            <button type="button" onClick={() => setIsStoreDropdownOpen(!isStoreDropdownOpen)}
                                    className="flex w-full items-center justify-between rounded-lg ring-1 ring-inset ring-stone-300 bg-white px-3 py-2 text-sm text-gray-900 hover:bg-stone-50 focus:ring-2 focus:ring-gray-900 transition-colors">
                                                        <span className={orderData.store_id ? 'text-gray-900' : 'text-stone-500'}>
                                                            {orderData.store_id
                                                                ? stores?.find(s => s.id === orderData.store_id)?.name
                                                                : 'Select a store...'}
                                                        </span>
                              <ChevronUpDownIcon className="h-5 w-5 text-stone-400" />
                            </button>
                            {isStoreDropdownOpen && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setIsStoreDropdownOpen(false)} />
                                  <div className="absolute z-20 mt-1 w-full rounded-lg bg-white shadow-lg ring-1 ring-stone-200">
                                    <div className="max-h-60 overflow-y-auto py-1">
                                      {!stores?.length ? (
                                          <div className="px-3 py-4 text-center text-sm text-stone-500">No stores available</div>
                                      ) : stores.map(store => {
                                        const isSelected = orderData.store_id === store.id;
                                        return (
                                            <button key={store.id} type="button"
                                                    onClick={() => {
                                                      onOrderChange({ target: { name: 'store_id', value: store.id } } as React.ChangeEvent<HTMLSelectElement>);
                                                      setIsStoreDropdownOpen(false);
                                                    }}
                                                    className={`flex w-full items-center px-3 py-2.5 text-left text-sm transition-colors border-b border-stone-50 last:border-0 ${
                                                        isSelected ? 'bg-stone-50 font-semibold text-gray-900' : 'text-gray-700 hover:bg-stone-50'
                                                    }`}>
                                              <span className="flex-1 truncate">{store.name}</span>
                                              {isSelected && <CheckIcon className="h-4 w-4 text-gray-900" />}
                                            </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </>
                            )}
                          </div>
                        </div>

                        {orderData.store_id && allProducts.length > 0 && (
                            <p className="mt-1 text-xs text-stone-400 sr-only">
                              {allProducts.length} products available
                            </p>
                        )}

                        <div className="col-span-2">
                          <label className={LABEL}>Fulfilment Type</label>
                          <div className="mt-1 grid grid-cols-2 gap-3">
                            {deliveryOptions.length === 0 ? (
                                <p className="text-sm text-stone-400 col-span-2">Loading options...</p>
                            ) : deliveryOptions.map(option => {
                              const isSelected   = orderData.delivery_type === option.type;
                              const isCollection = option.type === 'collection';
                              return (
                                  <button key={option.id} type="button"
                                          onClick={() => {
                                            onOrderChange({ target: { name: 'delivery_type', value: option.type } } as React.ChangeEvent<HTMLSelectElement>);
                                            onOrderChange({ target: { name: 'delivery_fee', value: String(option.amount) } } as React.ChangeEvent<HTMLSelectElement>);
                                            if (!isCollection) {
                                              onOrderChange({ target: { name: 'collection_point_id', value: '' } } as React.ChangeEvent<HTMLSelectElement>);
                                            }
                                          }}
                                          className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-all ${
                                              isSelected
                                                  ? isCollection
                                                      ? 'border-[#ee2b74] bg-pink-50 ring-1 ring-[#ee2b74]'
                                                      : 'border-gray-900 bg-stone-50 ring-1 ring-gray-900'
                                                  : 'border-stone-300 bg-white hover:bg-stone-50'
                                          }`}>
                                    <div className="flex-1">
                                      <p className={`font-semibold capitalize ${isSelected ? isCollection ? 'text-[#ee2b74]' : 'text-gray-900' : 'text-gray-700'}`}>
                                        {option.type.replace(/_/g, ' ')}
                                      </p>
                                      <p className="text-xs text-stone-500 mt-0.5">
                                        {option.amount === 0 ? 'Free' : `R${(option.amount / 100).toFixed(2)}`}
                                      </p>
                                    </div>
                                    {isSelected && <CheckIcon className="h-4 w-4 text-current flex-shrink-0" />}
                                  </button>
                              );
                            })}
                          </div>
                          {!orderData.delivery_type && (
                              <p className="mt-1 text-xs text-red-500">Please select a fulfilment type</p>
                          )}
                        </div>

                        {isCollectionSelected && (
                            <div className="col-span-2">
                              <label className={LABEL}>Collection Point <span className="text-red-500">*</span></label>
                              <div className="mt-1 space-y-2">
                                {collectionPoints.length === 0 ? (
                                    <div className="rounded-lg border border-dashed border-stone-300 p-4 text-center">
                                      <p className="text-sm text-stone-400">No active collection points available.</p>
                                      <p className="text-xs text-stone-300 mt-0.5">Add collection points under Settings → Collection Points.</p>
                                    </div>
                                ) : collectionPoints.map(point => {
                                  const isSelected = orderData.collection_point_id === point.id;
                                  return (
                                      <button key={point.id} type="button"
                                              onClick={() => onOrderChange({ target: { name: 'collection_point_id', value: point.id } } as React.ChangeEvent<HTMLSelectElement>)}
                                              className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-all ${
                                                  isSelected
                                                      ? 'border-[#ee2b74] bg-pink-50 ring-1 ring-[#ee2b74]'
                                                      : 'border-stone-300 bg-white hover:bg-stone-50'
                                              }`}>
                                        <div className="flex-1 min-w-0">
                                          <p className={`font-semibold truncate ${isSelected ? 'text-[#ee2b74]' : 'text-gray-900'}`}>{point.name}</p>
                                          <p className="text-xs text-stone-500 mt-0.5 truncate">{point.address_line1}</p>
                                        </div>
                                        {isSelected && <CheckIcon className="h-4 w-4 text-[#ee2b74] flex-shrink-0" />}
                                      </button>
                                  );
                                })}
                              </div>
                              {!orderData.collection_point_id && (
                                  <p className="mt-1 text-xs text-red-500">Please select a collection point</p>
                              )}
                            </div>
                        )}

                        <div className="col-span-2">
                          <label htmlFor="notes" className={LABEL}>Order Details / Notes</label>
                          <textarea id="notes" name="notes" rows={4}
                                    value={orderData.notes} onChange={onOrderChange}
                                    className={`${FIELD} resize-none`}
                                    placeholder="Enter order details, special instructions, or item list..."
                                    maxLength={1500} />
                          <div className="mt-1 text-right">
                                                    <span className={`text-xs ${(orderData.notes?.length ?? 0) > 1400 ? 'text-red-500' : 'text-stone-400'}`}>
                                                        {orderData.notes?.length ?? 0} / 1500
                                                    </span>
                          </div>
                        </div>
                      </div>

                      <OrderItemsSection
                          storeId={orderData.store_id}
                          items={orderItems}
                          onItemsChange={handleItemsChange}
                      />

                      <div className="border-t border-stone-200 pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-semibold text-gray-900">Customer</h4>
                          <button type="button" onClick={onToggleCustomerForm}
                                  className="text-xs font-medium text-[#ee2b74] hover:underline">
                            {showNewCustomerForm ? '← Search existing' : '+ New customer'}
                          </button>
                        </div>

                        {!showNewCustomerForm ? (
                            <Combobox as="div" value={orderData.customer_id}
                                      onChange={(value: string | null) => {
                                        onOrderChange({ target: { name: 'customer_id', value: value ?? '' } } as React.ChangeEvent<HTMLSelectElement>);
                                      }}>
                              <div className="relative">
                                <div className="relative">
                                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                                  <ComboboxInput className={`${FIELD} pl-10`}
                                                 placeholder="Search by name..."
                                                 onChange={e => setQuery(e.target.value)}
                                                 displayValue={(id: string) => {
                                                   const c = customers?.find(c => c.id === id);
                                                   return c ? `${c.first_name} ${c.last_name}` : '';
                                                 }} />
                                </div>
                                <Transition as={Fragment} leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100" leaveTo="opacity-0" afterLeave={() => setQuery('')}>
                                  <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    {query !== '' && !filteredCustomers?.length ? (
                                        <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                                          <button type="button" onClick={onToggleCustomerForm}
                                                  className="flex w-full items-center gap-3 py-2 text-left">
                                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-stone-100 text-stone-500">
                                              <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                              </svg>
                                            </div>
                                            <div>
                                              <p className="font-medium">No customer found for "{query}"</p>
                                              <p className="text-xs text-stone-400">Click here to create a new profile</p>
                                            </div>
                                          </button>
                                        </div>
                                    ) : filteredCustomers?.map(customer => (
                                        <ComboboxOption key={customer.id} value={customer.id}
                                                        className={({ focus }) => `relative cursor-default select-none py-2 pl-10 pr-4 ${focus ? 'bg-stone-50 text-stone-700' : 'text-gray-900'}`}>
                                          {({ selected, focus }) => (
                                              <>
                                                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                    {customer.first_name} {customer.last_name}
                                                </span>
                                                {selected && (
                                                    <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${focus ? 'text-stone-700' : 'text-gray-900'}`}>
                                                        <CheckIcon className="h-5 w-5" />
                                                    </span>
                                                )}
                                              </>
                                          )}
                                        </ComboboxOption>
                                    ))}
                                  </ComboboxOptions>
                                </Transition>
                              </div>
                            </Combobox>
                        ) : (
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <label htmlFor="first_name" className={LABEL}>First name</label>
                                  <input type="text" id="first_name" name="first_name" required value={newCustomer.first_name} onChange={onCustomerChange} className={FIELD} />
                                </div>
                                <div>
                                  <label htmlFor="last_name" className={LABEL}>Last name</label>
                                  <input type="text" id="last_name" name="last_name" required value={newCustomer.last_name} onChange={onCustomerChange} className={FIELD} />
                                </div>
                              </div>
                              <div>
                                <label htmlFor="cellphone" className={LABEL}>Cell phone</label>
                                <input type="text" id="cellphone" name="cellphone" required value={newCustomer.cellphone} onChange={onCustomerChange} className={FIELD} placeholder="e.g. 082 123 4567" />
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <label htmlFor="address_1" className={LABEL}>Address 1</label>
                                  <input type="text" id="address_1" name="address.address_1" required value={newCustomer.address.address_1} onChange={onCustomerChange} className={FIELD} />
                                </div>
                                <div>
                                  <label htmlFor="address_2" className={LABEL}>Address 2</label>
                                  <input type="text" id="address_2" name="address.address_2" required value={newCustomer.address.address_2} onChange={onCustomerChange} className={FIELD} />
                                </div>
                                <div className="sm:col-span-2">
                                  <label htmlFor="suburb" className={LABEL}>Suburb</label>
                                  <input type="text" id="suburb" name="address.suburb" required value={newCustomer.address.suburb} onChange={onCustomerChange} className={FIELD} />
                                </div>
                              </div>
                            </div>
                        )}
                      </div>

                      <div className="pt-2 flex items-center justify-end gap-3">
                        <button type="button" onClick={onClose}
                                className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-stone-300 hover:bg-stone-50">
                          Cancel
                        </button>
                        <button type="submit" disabled={isSubmitDisabled} aria-busy={loading}
                                className="inline-flex items-center rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed">
                          {loading ? 'Creating…' : 'Create order'}
                        </button>
                      </div>
                    </form>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
  );
};

export default CreateOrderModal;
